import React, { useContext, useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { NotificationContext } from "../context/NotificationContext";
import { apiClient } from "../apiclient/apiclient";

const NotificationBell = () => {
  const { notifications, setNotifications } = useContext(NotificationContext);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const isMainAdmin = user?.role === "admin" || user?.role === "superAdmin";

  const unreadCount = notifications.filter((n) => !n.read).length;

  /* LOAD UNREAD NOTIFICATIONS ON LOGIN */
  useEffect(() => {
    const loadUnread = async () => {
      try {
        const res = await apiClient.get("/notifications?unread=true&limit=8");
        setNotifications(res.data.data || []);
      } catch (e) {
        console.error("Failed to load notifications", e);
      }
    };

    loadUnread();
  }, [setNotifications]);

  /* MARK ALL AS READ */
  const handleClearAll = async () => {
    try {
      await apiClient.put("/notifications/mark-all-read");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) {
      console.error("Failed to clear notifications", e);
    }
  };

  /* MARK SINGLE AS READ */
  const handleMarkRead = async (id) => {
    try {
      await apiClient.put(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, read: true } : n)),
      );
    } catch (e) {
      console.error("Failed to mark notification as read", e);
    }
  };

  return (
    <div className="relative">
      {/* BELL BUTTON */}
      <button
        onClick={() => setIsOpen((o) => !o)}
        className="relative p-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 transition-colors"
      >
        <Bell size={20} className="text-white" />

        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] h-5 w-5 flex items-center justify-center rounded-full font-bold leading-none shadow-sm">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* DROPDOWN */}
      {isOpen && (
        <>
          <style>{`
            @keyframes notificationShow {
              from {
                opacity: 0;
                transform: translateY(-8px) scale(0.95);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
            .animate-notification {
              animation: notificationShow 0.15s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              transform-origin: top right;
            }
          `}</style>
          <div
            className="fixed inset-x-0 top-14 sm:absolute sm:right-0 sm:inset-auto sm:mt-2 w-full sm:w-80 bg-white rounded-none sm:rounded-lg shadow-lg z-50 border border-gray-200 animate-notification"
            style={{
              position: window.innerWidth < 640 ? "fixed" : "absolute",
            }}
          >
            {/* POINTER (Desktop only) */}
            {window.innerWidth >= 640 && (
              <div
                className="absolute -top-2 right-3 w-4 h-4 bg-white border-t border-r border-gray-200"
                style={{
                  transform: "rotate(45deg)",
                }}
              ></div>
            )}

          {/* HEADER */}
          <div className="p-3 border-b border-gray-200 flex justify-between items-center">
            <span className="font-semibold text-gray-900">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={handleClearAll}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Mark All
              </button>
            )}
          </div>

          {/* LIST */}
          <div className="max-h-80 overflow-y-auto [&::-webkit-scrollbar]:hidden">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                No notifications
              </div>
            ) : (
              notifications.slice(0, 10).map((n) => (
                <div
                  key={n._id}
                  className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                    !n.read ? "bg-blue-50" : ""
                  }`}
                  onClick={() => {
                    handleMarkRead(n._id);
                    setIsOpen(false);

                    if (n.data) {
                      if (n.activityType === "order" && n.data.orderId) {
                        navigate(`/orders/track-orders?id=${n.data.orderId}`);
                      } else if (n.activityType === "client" && n.data.clientId) {
                        navigate(`/manage-client/client?highlight=${n.data.clientId}`);
                      } else if (n.activityType === "payment" && n.data.clientId) {
                        navigate(`/manage-client/client-ledger/${n.data.clientId}`);
                      } else if (n.activityType === "product") {
                        if (n.data.productId) {
                          navigate(`/manage-products/products?highlight=${n.data.productId}`);
                        } else {
                          navigate("/manage-products/products");
                        }
                      }
                    }
                  }}
                >
                  <p className="text-sm text-gray-900">{n.message}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(n.createdAt).toLocaleString()}
                  </p>
                </div>
              ))
            )}
          </div>

          {/* FOOTER */}
          {isMainAdmin && (
            <div className="border-t border-gray-200 p-2.5 text-center">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate("/activity");
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View All
              </button>
            </div>
          )}
        </div>
      </>
    )}
  </div>
  );
};

export default NotificationBell;
